"use client";

import { PromptToolWorkbench } from "@saas/shared/components/PromptToolWorkbench";
import {
	PENDING_GENERATOR_STATE_KEY,
	saveBlobPendingState,
} from "@shared/lib/pending-state";
import { Button } from "@ui/components/button";
import {
	AlertCircle,
	Box,
	CheckCircle,
	Clock,
	Globe,
	ImageIcon,
	InfinityIcon,
	Loader2,
	X,
	Zap,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useDropzone } from "react-dropzone";
import { useBatchImageToPrompt } from "../hooks/use-batch-image-to-prompt";
import type { AIModel } from "../types";
import { BatchPromptResult } from "./BatchPromptResult";
import { LanguageSelector } from "./LanguageSelector";

export function BatchImagePromptConverter() {
	const t = useTranslations();
	const router = useRouter();
	const {
		imageFiles,
		selectedModel,
		promptLanguage,
		results,
		processingStatus,
		usesRemaining,
		hasUnlimitedCredits,
		isPending,
		handleImagesSelected,
		removeImage,
		clearImages,
		processBatch,
		setSelectedModel,
		setPromptLanguage,
		user,
	} = useBatchImageToPrompt();

	// Handle button click for generation
	const handleGenerateClick = () => {
		processBatch();
	};

	// Handle navigation to image generator
	const handleNavigateToImageGenerator = async (prompt: string) => {
		// Prepare generator state, switch to text-to-image tab
		const generatorState = {
			timestamp: Date.now(),
			activeTab: "text-to-image" as any,
			prompt: prompt,
			source: "batch-image-to-prompt" as any,
		};

		// Save state to localStorage
		if (typeof window !== "undefined") {
			await saveBlobPendingState(
				PENDING_GENERATOR_STATE_KEY,
				generatorState,
			);
		}

		// Navigate to image generator
		router.push("/ai-edit-image");
	};

	// Handle getting more credits
	const handleGetMoreCredits = () => {
		router.push("/subscription");
	};

	// File upload handler
	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop: (acceptedFiles) => {
			if (acceptedFiles.length > 0) {
				// Combine with existing images
				handleImagesSelected([...imageFiles, ...acceptedFiles]);
			}
		},
		accept: {
			"image/png": [".png"],
			"image/jpeg": [".jpg", ".jpeg"],
			"image/webp": [".webp"],
		},
		maxSize: 30 * 1024 * 1024, // 30MB
		multiple: true,
	});

	return (
		<PromptToolWorkbench
			title={t("batchImageToPrompt.title")}
			description={t("batchImageToPrompt.description")}
			action={
				<Button
					variant="primary"
					className="w-full h-12 rounded-full"
					disabled={
						isPending ||
						imageFiles.length === 0 ||
						(!!user &&
							usesRemaining !== "infinity" &&
							typeof usesRemaining === "number" &&
							usesRemaining < imageFiles.length)
					}
					onClick={handleGenerateClick}
				>
					{isPending ? (
						<>
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							{t(
								"batchImageToPrompt.components.converter.processing",
							)}
						</>
					) : (
						t(
							"batchImageToPrompt.components.converter.generatePrompts",
						)
					)}
				</Button>
			}
			result={
				results.length > 0 && (
					<BatchPromptResult
						results={results}
						imageFiles={imageFiles}
						onUseInImageGenerator={handleNavigateToImageGenerator}
					/>
				)
			}
			resultTitle={t("batchImageToPrompt.components.results.title")}
			emptyLabel={t("promptToolsRevamp.emptyPrompt")}
		>
			<div className="space-y-3">
				<div
					{...getRootProps()}
					className={`
										h-44 border-2 border-dashed rounded-md flex flex-col items-center justify-center cursor-pointer
										${isDragActive ? "border-indigo-400 bg-indigo-50" : "border-gray-200 hover:border-indigo-300"}
									`}
				>
					<input {...getInputProps()} />
					<ImageIcon className="h-12 w-12 text-indigo-400 mb-3" />
					<p className="text-gray-500 text-center mb-1">
						{t(
							"imageToPrompt.components.uploadTabs.uploadInstruction",
						)}
					</p>
					<p className="text-xs text-gray-400">
						{t("imageToPrompt.components.uploadTabs.fileTypes", {
							size: 30,
						})}
					</p>
				</div>
				{imageFiles.length > 0 && (
					<div className="overflow-hidden rounded-xl border bg-muted/20">
						{imageFiles.length > 0 ? (
							<div className="p-4 flex-1 overflow-auto">
								<div className="flex justify-between items-center mb-3">
									<span className="text-sm font-medium text-gray-700">
										{t(
											"batchImageToPrompt.components.uploader.uploadedImages",
											{ count: imageFiles.length },
										)}
									</span>
									<Button
										variant="ghost"
										size="sm"
										onClick={clearImages}
										className="text-red-500 hover:text-red-600 hover:bg-red-50 text-xs"
									>
										{t(
											"batchImageToPrompt.components.uploader.clearAll",
										)}
									</Button>
								</div>

								<div className="max-h-[340px] overflow-y-auto pr-1">
									<div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
										{imageFiles.map((file, index) => (
											<div
												key={`${file.name}-${index}`}
												className="relative border rounded-md overflow-hidden group shadow-sm"
											>
												<div className="w-full aspect-square">
													<img
														src={URL.createObjectURL(
															file,
														)}
														alt={file.name}
														className="w-full h-full object-cover"
													/>
												</div>
												<div className="absolute top-1 right-1 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100">
													<Button
														variant="ghost"
														size="icon"
														aria-label={`${t("shared.actions.remove")}: ${file.name}`}
														onClick={() =>
															removeImage(index)
														}
														className="bg-white/90 hover:bg-white text-red-500 h-5 w-5 rounded-full p-0"
													>
														<X className="h-3 w-3" />
													</Button>
												</div>
											</div>
										))}
									</div>
								</div>
							</div>
						) : (
							<div className="flex-1 flex items-center justify-center text-gray-400 p-4">
								<div className="text-center">
									<ImageIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
									<p>
										{t(
											"batchImageToPrompt.components.uploader.noImagesUploaded",
										)}
									</p>
								</div>
							</div>
						)}
					</div>
				)}
			</div>
			<div className="space-y-3">
				<h2 className="text-sm font-medium">
					{t("batchImageToPrompt.components.converter.promptTarget")}
				</h2>
				<div className="grid grid-cols-2 gap-2">
					{[
						{
							id: "flux" as AIModel,
							icon: Zap,
							label: t(
								"imageToPrompt.components.modelLabels.flux",
							),
						},
						{
							id: "general" as AIModel,
							icon: Globe,
							label: t(
								"imageToPrompt.components.modelLabels.general",
							),
						},
						{
							id: "stable-diffusion" as AIModel,
							icon: Box,
							label: t(
								"imageToPrompt.components.modelLabels.stableDiffusion",
							),
						},
						{
							id: "midjourney" as AIModel,
							icon: ImageIcon,
							label: t(
								"imageToPrompt.components.modelLabels.midjourney",
							),
						},
					].map((model) => (
						<button
							key={model.id}
							type="button"
							onClick={() => setSelectedModel(model.id)}
							className={`relative flex items-center justify-center gap-1 p-3 rounded-lg transition-colors bg-gray-50 hover:bg-gray-100 ${
								selectedModel === model.id
									? "border-2 border-[#6366F1]"
									: "border border-gray-200"
							}`}
						>
							{selectedModel === model.id && (
								<div className="absolute top-2 right-2 text-[#6366F1]">
									<CheckCircle
										size={16}
										aria-label="Selected"
									/>
								</div>
							)}
							<model.icon
								className={
									selectedModel === model.id
										? "text-[#6366F1]"
										: "text-gray-500"
								}
								size={20}
								aria-label={`${model.label} icon`}
								aria-hidden="true"
							/>
							<span className="text-xs sm:text-sm font-medium whitespace-normal">
								{model.label}
							</span>
						</button>
					))}
				</div>
			</div>
			<div className="space-y-3">
				<h2 className="text-sm font-medium">
					{t("batchImageToPrompt.components.converter.language")}
				</h2>
				<LanguageSelector
					value={promptLanguage}
					onChange={setPromptLanguage}
				/>
			</div>
			{user && (
				<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
					<div className="flex items-center gap-2 flex-wrap">
						<span className="font-medium text-sm">
							{t(
								"batchImageToPrompt.components.converter.creditsRemaining",
							)}
							:
						</span>
						<span className="flex items-center text-indigo-600 font-bold">
							{usesRemaining === "infinity" ? (
								<InfinityIcon className="h-4 w-4 text-indigo-500" />
							) : (
								<span>{usesRemaining} 💎</span>
							)}
						</span>
						<Button
							variant="link"
							size="sm"
							onClick={handleGetMoreCredits}
							className="text-indigo-600 px-1 h-7"
						>
							{t(
								"batchImageToPrompt.components.converter.getMoreCredits",
							)}
						</Button>
					</div>

					<Button
						variant="link"
						size="sm"
						onClick={() =>
							window.dispatchEvent(new Event("openPromptHistory"))
						}
						className="text-indigo-600 flex items-center gap-1 h-7"
					>
						<Clock className="h-4 w-4" />
						{t(
							"imageToPrompt.components.promptConverter.viewHistory",
						)}
					</Button>
				</div>
			)}
			{user &&
				imageFiles.length > 0 &&
				usesRemaining !== "infinity" &&
				typeof usesRemaining === "number" &&
				usesRemaining < imageFiles.length && (
					<div className="flex items-center gap-2 p-2 bg-amber-50 text-amber-800 rounded-md text-sm">
						<AlertCircle className="h-4 w-4 flex-shrink-0" />
						<span>
							{t(
								"batchImageToPrompt.components.converter.notEnoughCredits",
								{
									needed: imageFiles.length,
									available: usesRemaining,
								},
							)}
						</span>
					</div>
				)}
		</PromptToolWorkbench>
	);
}
